import { Container, Stack, Title, Text, Paper, Button, Group, Alert, Badge, Divider, ActionIcon, SegmentedControl, SimpleGrid, Box, Anchor, Skeleton } from '@mantine/core';
import { IconCreditCard, IconCalendar, IconCheck, IconClock, IconAlertTriangle, IconChevronLeft, IconSparkles, IconInfoCircle } from '@tabler/icons-react';
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSubscription } from '../hooks/useSubscription';
import { VoucherRedemption } from '../components/Billing/VoucherRedemption';
import { CancelTrialModal } from '../components/Billing/CancelTrialModal';
import { PLAN_PRICES } from '../constants/pricing';
import dayjs from 'dayjs';

const FEATURES = [
  'Personalized weekly meal plans',
  'Intelligent recipe suggestions',
  'Grocery shopping integration',
  'Recipe collection management',
  'Pantry tracking & meal prepping',
];

const MONTHLY_PRICE = PLAN_PRICES.monthly;
const ANNUAL_PRICE = PLAN_PRICES.annual;

const formatDate = (dateString?: string) => {
  if (!dateString) return '';
  return dayjs(dateString).format('MMM D, YYYY');
};

const PricingCard = ({ selectedPlan, onPlanChange, onSubscribe, isLoading, currentPlan }: {
  selectedPlan: 'monthly' | 'annual';
  onPlanChange: (plan: 'monthly' | 'annual') => void;
  onSubscribe: () => void;
  isLoading: boolean;
  currentPlan?: 'monthly' | 'annual';
}) => {
  const isCurrentPlan = currentPlan === selectedPlan;
  const price = selectedPlan === 'monthly' ? MONTHLY_PRICE : ANNUAL_PRICE;
  const savings = (MONTHLY_PRICE * 12) - ANNUAL_PRICE;
  
  return (
    <Paper 
      radius="xl"
      p="xl"
      style={{
        background: 'linear-gradient(135deg, var(--mantine-color-orange-6) 0%, var(--mantine-color-red-6) 100%)',
        border: 'none',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(255, 146, 43, 0.3)'
      }}
    >
      {/* Decorative elements */}
      <Box
        style={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 120,
          height: 120,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          zIndex: 1
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: -20,
          left: -20,
          width: 80,
          height: 80,
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '50%',
          zIndex: 1
        }}
      />

      <Stack gap="xl" style={{ position: 'relative', zIndex: 2 }}>
        <Stack gap="xs" align="center" style={{ minHeight: '70px' }}>
          <Group gap="xs" align="center">
            <IconSparkles size={20} style={{ color: '#fbbf24' }} />
            <Title order={2} c="white" fw={700} ta="center">
              Choose your plan
            </Title>
          </Group>
          {selectedPlan === 'annual' && (
            <Badge 
              variant="white" 
              color="orange" 
              size="md"
              style={{ 
                backgroundColor: 'var(--mantine-color-orange-6)',
                color: 'white',
                fontWeight: 700
              }}
            >
              Save ${savings.toFixed(0)}
            </Badge>
          )}
        </Stack>
        
        <Stack gap="lg" align="center">
          <SegmentedControl
            value={selectedPlan}
            onChange={(value) => onPlanChange(value as 'monthly' | 'annual')}
            data={[
              { label: 'Monthly', value: 'monthly' },
              { label: 'Annual', value: 'annual' }
            ]}
            size="md"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              padding: '4px'
            }}
            styles={{
              control: {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
              indicator: {
                backgroundColor: 'var(--mantine-color-orange-4)',
                color: 'white'
              },
              label: {
                color: 'white',
                fontWeight: 600
              }
            }}
          />
          
          <Stack gap="xs" align="center">
            <Group align="baseline" gap="xs">
              <Title order={1} c="white" fw={800} size="3rem">
                ${price}
              </Title>
              <Text c="rgba(255,255,255,0.8)" size="lg" fw={500}>
                /{selectedPlan === 'monthly' ? 'month' : 'year'}
              </Text>
            </Group>
          </Stack>
        </Stack>

        <Divider color="rgba(255,255,255,0.2)" />

        <Box style={{ alignSelf: 'center' }}>
          <Stack gap="md">
            {FEATURES.map((feature, index) => (
              <Group key={index} gap="sm" wrap="nowrap" align="flex-start">
                <IconCheck size={18} style={{ color: '#fbbf24', flexShrink: 0, marginTop: 2 }} />
                <Text c="white" fw={500}>{feature}</Text>
              </Group>
            ))}
          </Stack>
        </Box>

        <Button 
          size="xl"
          variant={isCurrentPlan ? "light" : "filled"}
          disabled={isCurrentPlan || isLoading}
          loading={isLoading}
          onClick={onSubscribe}
          mx="auto"
          style={{
            background: isCurrentPlan 
              ? 'rgba(255,255,255,0.2)' 
              : 'var(--mantine-color-orange-4)',
            color: 'white',
            fontWeight: 600,
            fontSize: '16px',
            height: '48px',
            borderRadius: '8px',
            border: 'none',
            transition: 'all 0.2s ease',
            textTransform: 'none'
          }}
        >
          {isCurrentPlan ? 'Current plan' : `Subscribe ${selectedPlan === 'monthly' ? 'monthly' : 'annually'}`}
        </Button>
      </Stack>
    </Paper>
  );
};

export const BillingPage = () => {
  usePageTitle('Billing | Meals You Love');
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly');
  const [changingToPlan, setChangingToPlan] = useState<'monthly' | 'annual' | null>(null);
  const [cancelTrialModalOpen, setCancelTrialModalOpen] = useState(false);
  const {
    subscription,
    isLoading,
    getTrialDaysRemaining,
    isTrialExpiringSoon,
    isTrialCanceled,
    createCheckout,
    openBillingPortal,
    cancelSubscription,
    reactivateSubscription,
    changePlan,
    cancelTrial,
    resumeTrial,
    isCreatingCheckout,
    isOpeningBillingPortal,
    isCanceling,
    isReactivating,
    isChangingPlan,
    isCancelingTrial,
    isResumingTrial,
    refetch
  } = useSubscription();

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubscribe = () => {
    createCheckout(selectedPlan);
  };

  const handleChangePlan = (planType: 'monthly' | 'annual') => {
    setChangingToPlan(planType);
    changePlan(planType);
  };

  const handleVoucherSuccess = () => {
    refetch();
  };

  const handleCancelTrial = (reason?: string) => {
    cancelTrial(reason);
    setCancelTrialModalOpen(false);
  };

  // Reset changing plan state when not changing anymore
  React.useEffect(() => {
    if (!isChangingPlan) {
      setChangingToPlan(null);
    }
  }, [isChangingPlan]);

  const trialDaysRemaining = getTrialDaysRemaining();

  const renderTrialStatus = () => {
    if (subscription?.status !== 'trial') return null;

    const isExpired = trialDaysRemaining <= 0;
    const trialEndsOn = subscription.trialEndsAt ? formatDate(subscription.trialEndsAt) : undefined;

    // Canceled trial: they've told us they're done, so this stops selling and
    // becomes the receipt — no charge happened, and here's when access ends.
    if (isTrialCanceled) {
      return (
        <Paper withBorder p="lg" radius="lg">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <div>
                <Title order={3}>Trial canceled</Title>
                <Text c="dimmed">
                  {isExpired
                    ? 'Your free trial has ended.'
                    : `You'll keep full access${trialEndsOn ? ` until ${trialEndsOn}` : ' until your trial ends'}.`}
                </Text>
              </div>
              <Badge variant="light" color="gray">Canceled</Badge>
            </Group>

            <Alert icon={<IconInfoCircle size={16} />} color="gray" title="Nothing was charged" radius="lg">
              We never collected a card for your trial, so there's no payment to
              stop and nothing to refund. We've also turned off the trial
              reminder emails.
            </Alert>

            {!isExpired && (
              <Group gap="md">
                <Button
                  variant="light"
                  onClick={() => resumeTrial()}
                  loading={isResumingTrial}
                  w={{ base: '100%', sm: 'auto' }}
                >
                  Changed your mind? Resume trial
                </Button>
              </Group>
            )}
          </Stack>
        </Paper>
      );
    }

    const isExpiringSoon = isTrialExpiringSoon;
    const color = isExpired ? 'red' : isExpiringSoon ? 'yellow' : 'blue';

    return (
      <Alert
        icon={isExpiringSoon || isExpired ? <IconClock size={16} /> : <IconCheck size={16} />}
        color={color}
        title={isExpired ? 'Trial expired' : `Trial Active - ${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining`}
        radius="lg"
        style={{
          border: `1px solid var(--mantine-color-${color}-3)`,
        }}
      >
        <Stack gap="xs">
          {isExpired ? (
            <Text c="red.7">Your free trial has ended. Subscribe to continue using Meals You Love.</Text>
          ) : isExpiringSoon ? (
            <Text>Your trial expires soon. Subscribe now to avoid any interruption to your meal planning!</Text>
          ) : (
            <Text>
              You're currently enjoying a free one-month trial.
              {trialEndsOn && ` Your trial expires on ${trialEndsOn}.`}
            </Text>
          )}

          {/* The question people actually write in about. Say it before they
              have to ask, on every trial state. */}
          <Text size="sm" c="dimmed">
            {isExpired
              ? "We never had a card on file, so you weren't charged anything when your trial ended."
              : "We don't have a card on file, so nothing will be charged when your trial ends. It just stops unless you subscribe."}
          </Text>

          {!isExpired && (
            <Anchor
              component="button"
              type="button"
              size="sm"
              c="dimmed"
              onClick={() => setCancelTrialModalOpen(true)}
              style={{ alignSelf: 'flex-start' }}
            >
              Want to cancel your trial?
            </Anchor>
          )}
        </Stack>
      </Alert>
    );
  };

  const renderCurrentSubscription = () => {
    // past_due belongs here as much as active does. It used to return null and
    // fall through to the acquisition block, so a paying customer whose card
    // declined was pitched "choose your plan" and told nothing about the
    // subscription they already had.
    const isPastDue = subscription?.status === 'past_due';
    if (subscription?.status !== 'active' && !isPastDue) return null;

    // Handle lifetime subscription differently
    if (subscription.planType === 'lifetime') {
      return (
        <Paper withBorder p="lg" radius="lg">
          <Stack gap="md">
            <Group justify="space-between" align="center">
              <div>
                <Title order={3}>Lifetime access</Title>
                <Text c="dimmed">
                  You have lifetime access to Meals You Love
                </Text>
              </div>
              <Badge variant="gradient" gradient={{ from: 'gold', to: 'orange' }} size="lg">Lifetime</Badge>
            </Group>

            {subscription.lifetimeAccessGrantedAt && (
              <Group gap="xs">
                <IconCalendar size={16} />
                <Text size="sm">
                  Granted on {formatDate(subscription.lifetimeAccessGrantedAt)}
                </Text>
              </Group>
            )}

            <Alert color="green" title="All set!" radius="lg">
              You're all set with lifetime access. Enjoy unlimited meal planning without any recurring charges.
            </Alert>
          </Stack>
        </Paper>
      );
    }

    // Handle regular monthly/annual subscriptions
    return (
      <Paper withBorder p="lg" radius="lg">
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <div>
              <Title order={3}>Current subscription</Title>
              <Text c="dimmed">
                {subscription.planType === 'monthly' ? 'Monthly Plan' : 'Annual Plan'} - 
                ${subscription.planType === 'monthly' ? '9.99/month' : '99.99/year'}
              </Text>
            </div>
            <Badge variant="light" color={isPastDue ? 'red' : 'green'}>
              {isPastDue ? 'Past due' : 'Active'}
            </Badge>
          </Group>

          {/* Skipped while past due: currentPeriodEnd is the renewal that
              failed, so it's in the past and "Renews on" would read as a lie.
              The banner below carries the state instead. */}
          {subscription.currentPeriodEnd && !isPastDue && (
            <Group gap="xs">
              <IconCalendar size={16} />
              <Text size="sm">
                {subscription.cancelAtPeriodEnd 
                  ? `Cancels on ${formatDate(subscription.currentPeriodEnd)}`
                  : `Renews on ${formatDate(subscription.currentPeriodEnd)}`
                }
              </Text>
            </Group>
          )}

          {/* No plan switching while past due: changePlan rejects any
              subscription that isn't active, so the button could only fail. */}
          {!subscription.cancelAtPeriodEnd && !isPastDue && (
            <>
              <Divider />
              <div>
                <Group justify="space-between" align="center" mb="sm">
                  <Text fw={500}>Change plan</Text>
                  <Text size="sm" c="dimmed">
                    Switch between monthly and annual billing
                  </Text>
                </Group>
                {subscription.planType === 'monthly' ? (
                  <Button
                    variant="filled"
                    onClick={() => handleChangePlan('annual')}
                    loading={changingToPlan === 'annual'}
                    size="sm"
                    w={{ base: '100%', sm: 'auto' }}
                  >
                    Switch to Annual (Save $20)
                  </Button>
                ) : (
                  <Button
                    variant="filled"
                    onClick={() => handleChangePlan('monthly')}
                    loading={changingToPlan === 'monthly'}
                    size="sm"
                    w={{ base: '100%', sm: 'auto' }}
                  >
                    Switch to Monthly
                  </Button>
                )}
              </div>
            </>
          )}

          {/* Deliberately always available, not just once a payment has failed.
              An expiring card is the most common reason a subscription lapses,
              and the fix has to be reachable before the charge fails rather
              than after. Safe to expose now that the portal has cancellation
              and plan switching turned off, so it can't route around the cancel
              flow or the proration handling in /change-plan.

              Hidden while past due only to avoid a second button with the same
              label as the banner's, which goes to the same place. */}
          {!isPastDue && (
            <>
              <Divider />
              <div>
                <Group justify="space-between" align="center" mb="sm">
                  <Text fw={500}>Payment method</Text>
                  <Text size="sm" c="dimmed">
                    Update your card or view past invoices
                  </Text>
                </Group>
                <Button
                  variant="filled"
                  onClick={() => openBillingPortal()}
                  loading={isOpeningBillingPortal}
                  size="sm"
                  w={{ base: '100%', sm: 'auto' }}
                >
                  Update Payment Method
                </Button>
              </div>
            </>
          )}

          <Divider />

          <Group gap="md">
            {subscription.cancelAtPeriodEnd ? (
              <Button 
                variant="filled" 
                onClick={() => reactivateSubscription()}
                loading={isReactivating}
                w={{ base: '100%', sm: 'auto' }}
              >
                Reactivate Subscription
              </Button>
            ) : (
              <Button 
                variant="outline" 
                color="red" 
                onClick={() => cancelSubscription()}
                loading={isCanceling}
                w={{ base: '100%', sm: 'auto' }}
              >
                Cancel Subscription
              </Button>
            )}
          </Group>

          {subscription.cancelAtPeriodEnd && (
            <Alert color="yellow" title="Subscription scheduled for cancellation" radius="lg">
              Your subscription will be canceled at the end of the current billing period. 
              You'll continue to have access until {subscription.currentPeriodEnd && formatDate(subscription.currentPeriodEnd)}.
            </Alert>
          )}
        </Stack>
      </Paper>
    );
  };

  const renderPastDueStatus = () => {
    if (subscription?.status !== 'past_due') return null;

    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Payment failed" radius="lg">
        <Stack gap="sm">
          <Text>
            Your last payment failed and your subscription is past due.
            Update your card to settle the balance and restore access.
          </Text>
          <Group gap="md">
            <Button
              size="sm"
              onClick={() => openBillingPortal()}
              loading={isOpeningBillingPortal}
            >
              Update Payment Method
            </Button>
          </Group>
        </Stack>
      </Alert>
    );
  };

  if (isLoading || !subscription) {
    return (
      <Container size="lg" py="xl">
        <Stack gap="xl">
          {/* Header skeleton */}
          <div>
            <Group align="center" gap="md" mb="lg">
              <Skeleton height={34} width={34} radius="md" />
              <Skeleton height={32} width={100} />
            </Group>
            <Skeleton height={18} width="min(420px, 90%)" mb={4} />
          </div>

          {/* Subscription card skeleton */}
          <Paper withBorder p="lg" radius="lg">
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Stack gap={4}>
                  <Skeleton height={24} width={200} />
                  <Skeleton height={16} width={250} />
                </Stack>
                <Skeleton height={24} width={60} radius="xl" />
              </Group>
              <Group gap="xs">
                <Skeleton height={16} width={16} radius="sm" />
                <Skeleton height={14} width={200} />
              </Group>
              <Skeleton height={1} />
              <Stack gap="sm">
                <Group justify="space-between" align="center">
                  <Skeleton height={16} width={100} />
                  <Skeleton height={14} width={240} />
                </Group>
                <Skeleton height={36} w={{ base: '100%', sm: 200 }} radius="md" />
              </Stack>
              <Skeleton height={1} />
              <Skeleton height={36} w={{ base: '100%', sm: 180 }} radius="md" />
            </Stack>
          </Paper>
        </Stack>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      
      <Stack gap="xl">
        <div>
          <Group align="center" gap="md" mb="sm">
            <ActionIcon 
              variant="subtle" 
              onClick={handleBack}
              size="lg"
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Title order={1}>Billing</Title>
          </Group>
          <Text c="dimmed">Manage your Meals You Love subscription and billing preferences.</Text>
        </div>

        {renderTrialStatus()}
        {/* The past-due banner goes above the subscription card, not below it.
            past_due is the one status where both render, and the card ends in
            Cancel Subscription. Card first would put the destructive action
            ahead of the one that fixes the problem. */}
        {renderPastDueStatus()}
        {renderCurrentSubscription()}

        {/* Acquisition: plan picker, the pitch, and voucher redemption. Only for
            people who need to start a subscription. past_due used to land here
            because it isn't 'active', which pitched an existing customer on
            plans they already pay for, next to a Subscribe button the API now
            rejects with SUBSCRIPTION_NEEDS_PAYMENT_UPDATE. It also offered
            voucher redemption, which would grant lifetime access without
            cancelling the delinquent Stripe subscription still dunning them. */}
        {subscription &&
          (subscription.status === 'trial' || subscription.status === 'canceled') && (
          <>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={{ base: "xl", sm: "md" }}>
              <PricingCard
                selectedPlan={selectedPlan}
                onPlanChange={setSelectedPlan}
                onSubscribe={handleSubscribe}
                isLoading={isCreatingCheckout}
                currentPlan={undefined}
              />
              
              <Stack gap="md" h="100%">
                <Paper withBorder p="lg" radius="lg" style={{ border: '1px solid var(--mantine-color-orange-2)', background: 'rgba(255, 250, 245, 0.5)', flex: 1 }}>
                  <Stack gap="md" align="center" ta="center" h="100%" justify="center">
                    <Title order={3} c="dark">Why subscribe?</Title>
                    <Text c="dimmed" size="sm">
                      Your subscription helps us continue improving Meals You Love, adding new features, 
                      and helping more busy people reclaim their time and reduce mealtime stress.
                    </Text>
                    <Anchor component={Link} to="/about" c="orange" size="sm" fw={500}>
                      Learn more about our story
                    </Anchor>
                  </Stack>
                </Paper>
                
                <Box style={{ flex: 1 }}>
                  <VoucherRedemption 
                    onSuccess={handleVoucherSuccess}
                    disabled={isLoading || isCreatingCheckout}
                  />
                </Box>
              </Stack>
            </SimpleGrid>

            <Paper withBorder p="lg" radius="lg">
              <Stack gap="md">
                <Group gap="xs" justify="center" hiddenFrom="sm" wrap="nowrap">
                  <IconCreditCard size={20} style={{ flexShrink: 0 }} />
                  <Title order={3}>Secure payment</Title>
                </Group>
                <Group gap="xs" visibleFrom="sm">
                  <IconCreditCard size={20} />
                  <Title order={3}>Secure payment</Title>
                </Group>
                <Text c="dimmed" size="sm">
                  All payments are processed securely through Stripe. We never store your payment information on our servers.
                  You can cancel your subscription at any time from this page.
                  {subscription.status === 'trial' && ' Your free trial never asked for a card, so you\'ll only enter payment details if you decide to subscribe.'}
                </Text>
              </Stack>
            </Paper>
          </>
        )}
        <Text c="dimmed" size="sm" ta="center">
          Have billing questions or issues? Contact us at{' '}
          <Anchor href="mailto:billing@mealsyoulove.com" size="sm">billing@mealsyoulove.com</Anchor>
        </Text>
      </Stack>

      <CancelTrialModal
        opened={cancelTrialModalOpen}
        onClose={() => setCancelTrialModalOpen(false)}
        onConfirm={handleCancelTrial}
        isCanceling={isCancelingTrial}
        trialEndsOn={subscription.trialEndsAt ? formatDate(subscription.trialEndsAt) : undefined}
      />
    </Container>
  );
};
